export interface ClientBundleRefreshEnvironment {
  currentEntryAsset(): string | undefined;
  fetchIndexHtml(): Promise<{ html: string; baseUrl: string } | undefined>;
  reload(): void;
}

/** Extracts Vite's hashed browser entry from a freshly fetched index page. */
export function entryAssetFromHtml(html: string): string | undefined {
  const scriptTags = html.match(/<script\b[^>]*>/giu) ?? [];
  for (const tag of scriptTags) {
    const src = tag.match(/\bsrc\s*=\s*["']([^"']+)["']/iu)?.[1];
    if (src !== undefined && /\/assets\/index-[^/]+\.js(?:[?#].*)?$/u.test(src)) return src;
  }
  return undefined;
}

function browserRefreshEnvironment(): ClientBundleRefreshEnvironment {
  return {
    currentEntryAsset: () => {
      if (typeof document === 'undefined') return undefined;
      return document.querySelector<HTMLScriptElement>('script[src*="/assets/index-"][src$=".js"]')?.src;
    },
    fetchIndexHtml: async () => {
      if (typeof window === 'undefined' || typeof fetch === 'undefined') return undefined;
      const response = await fetch(new URL('/', window.location.href), {
        cache: 'no-store',
        credentials: 'same-origin',
        headers: { accept: 'text/html' },
      });
      if (!response.ok) return undefined;
      return {
        html: await response.text(),
        baseUrl: response.url.length > 0 ? response.url : window.location.href,
      };
    },
    reload: () => window.location.reload(),
  };
}

/**
 * Replaces a stale in-memory SPA after a container swap. A failed version
 * probe is deliberately non-terminal: ordinary WebSocket reconnect continues.
 */
export async function refreshIfClientBundleChanged(
  environment: ClientBundleRefreshEnvironment = browserRefreshEnvironment(),
): Promise<boolean> {
  try {
    const currentAsset = environment.currentEntryAsset();
    if (currentAsset === undefined) return false;
    const latest = await environment.fetchIndexHtml();
    if (latest === undefined) return false;
    const latestAsset = entryAssetFromHtml(latest.html);
    if (latestAsset === undefined) return false;
    const currentUrl = new URL(currentAsset, latest.baseUrl);
    const latestUrl = new URL(latestAsset, latest.baseUrl);
    if (currentUrl.href === latestUrl.href) return false;
    environment.reload();
    return true;
  } catch {
    return false;
  }
}
